import { useCallback, useEffect, useRef, useState } from "react";
import { getDrawCounts, KanaDrawCounts, setDrawCounts } from "../utils/kanaDrawCountsStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { useAuth } from "../context/AuthContext";
import { ProgressSyncStatus } from "./progressTypes";

const CLOUD_WRITE_DEBOUNCE_MS = 800;
const FEATURE_KEY = "kanaDrawCounts";

export function useKanaDrawCounts() {
    const { status, user } = useAuth();
    const [counts, setCountsState] = useState<KanaDrawCounts>(() => getDrawCounts());
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");
    const writeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestCountsRef = useRef(counts);
    latestCountsRef.current = counts;

    useEffect(() => {
        if (status !== "authenticated" || !user?.identityId) {
            setCountsState(getDrawCounts());
            setSyncStatus("local");
            return undefined;
        }

        let cancelled = false;
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                if (remote?.data && Object.keys(remote.data).length > 0) {
                    setCountsState(remote.data);
                }
                setSyncStatus("ready");
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [status, user?.identityId]);

    useEffect(() => {
        return () => {
            if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
        };
    }, []);

    const increment = useCallback(
        (symbol: string) => {
            setCountsState((current) => {
                const next = { ...current, [symbol]: (current[symbol] || 0) + 1 };
                setDrawCounts(next);

                if (status === "authenticated" && user?.identityId) {
                    if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
                    writeTimeoutRef.current = setTimeout(() => {
                        putCloudProgress(user.identityId, FEATURE_KEY, latestCountsRef.current).catch(() => {
                            setSyncStatus("error");
                        });
                    }, CLOUD_WRITE_DEBOUNCE_MS);
                }

                return next;
            });
        },
        [status, user?.identityId]
    );

    return { counts, increment, syncStatus };
}
